import { apiClient } from '../lib/apiClient';

/**
 * Legal document updates (backend src/routes/meLegal.routes.ts).
 *
 * Privacy policy update notice:
 *   GET  /api/v1/me/legal-status?lang=de
 *        → { data: { privacyPolicy: { currentVersion, effectiveDate, acknowledgedVersion,
 *                    needsAcknowledgement, changes: [{ version, effectiveDate, summary }] } } }
 *   POST /api/v1/me/legal-acknowledgements { document: 'privacy-policy', version }
 *        → 201 { data: { document, version, acknowledgedAt } }
 *
 * The acknowledgement is information, not consent (Art. 13 GDPR).
 *
 * Terms of service re-acceptance (consent):
 *   GET  /api/v1/me/legal-status → { data: { termsOfService: { currentVersion, effectiveDate,
 *                    acceptedVersion, needsAcceptance, restricted, declinedAt, changes } } }
 *   POST /api/v1/me/terms-of-service/acceptance { version } → 201
 *   POST /api/v1/me/terms-of-service/decline    { version } → 201
 */
export interface LegalChange {
  version: string;
  effectiveDate: string;
  summary: string;
}

export interface PrivacyPolicyStatus {
  currentVersion: string;
  effectiveDate: string;
  acknowledgedVersion: string | null;
  needsAcknowledgement: boolean;
  changes: LegalChange[];
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function parseChange(raw: unknown): LegalChange | null {
  const c = raw as Partial<Record<keyof LegalChange, unknown>> | null;
  if (!c || !isString(c.version) || !isString(c.effectiveDate) || !isString(c.summary)) return null;
  return { version: c.version, effectiveDate: c.effectiveDate, summary: c.summary };
}

/**
 * Defensive parse: anything unexpected (an older backend, a stub answering
 * `{ data: [] }`) reads as "nothing to show" — the notice must never block the
 * app because of a malformed answer.
 */
export function parsePrivacyPolicyStatus(body: unknown): PrivacyPolicyStatus | null {
  const p = (body as { data?: { privacyPolicy?: Record<string, unknown> } } | null)?.data?.privacyPolicy;
  if (!p || typeof p !== 'object' || !isString(p.currentVersion) || !isString(p.effectiveDate)) return null;
  const changes = Array.isArray(p.changes) ? p.changes.map(parseChange).filter((c): c is LegalChange => c !== null) : [];
  return {
    currentVersion: p.currentVersion,
    effectiveDate: p.effectiveDate,
    acknowledgedVersion: isString(p.acknowledgedVersion) ? p.acknowledgedVersion : null,
    needsAcknowledgement: p.needsAcknowledgement === true,
    changes,
  };
}

export async function fetchPrivacyPolicyStatus(lang: string, signal?: AbortSignal): Promise<PrivacyPolicyStatus | null> {
  const body = await apiClient.get<unknown>(`/api/v1/me/legal-status?lang=${encodeURIComponent(lang)}`, { signal });
  return parsePrivacyPolicyStatus(body);
}

export async function acknowledgePrivacyPolicy(version: string): Promise<void> {
  await apiClient.post('/api/v1/me/legal-acknowledgements', { document: 'privacy-policy', version });
}

export interface TermsOfServiceStatus {
  currentVersion: string;
  effectiveDate: string;
  acceptedVersion: string | null;
  needsAcceptance: boolean;
  /** The account is read-only until the current version is accepted. */
  restricted: boolean;
  /** ISO timestamp of a decline of the current version; null = not declined. */
  declinedAt: string | null;
  changes: LegalChange[];
}

/** Defensive parse like parsePrivacyPolicyStatus: anything unexpected reads as "nothing to ask". */
export function parseTermsOfServiceStatus(body: unknown): TermsOfServiceStatus | null {
  const p = (body as { data?: { termsOfService?: Record<string, unknown> } } | null)?.data?.termsOfService;
  if (!p || typeof p !== 'object' || !isString(p.currentVersion) || !isString(p.effectiveDate)) return null;
  const changes = Array.isArray(p.changes) ? p.changes.map(parseChange).filter((c): c is LegalChange => c !== null) : [];
  return {
    currentVersion: p.currentVersion,
    effectiveDate: p.effectiveDate,
    acceptedVersion: isString(p.acceptedVersion) ? p.acceptedVersion : null,
    needsAcceptance: p.needsAcceptance === true,
    restricted: p.restricted === true,
    declinedAt: isString(p.declinedAt) ? p.declinedAt : null,
    changes,
  };
}

export async function fetchTermsOfServiceStatus(lang: string, signal?: AbortSignal): Promise<TermsOfServiceStatus | null> {
  const body = await apiClient.get<unknown>(`/api/v1/me/legal-status?lang=${encodeURIComponent(lang)}`, { signal });
  return parseTermsOfServiceStatus(body);
}

export async function acceptTermsOfService(version: string): Promise<void> {
  await apiClient.post('/api/v1/me/terms-of-service/acceptance', { version });
}

export async function declineTermsOfService(version: string): Promise<void> {
  await apiClient.post('/api/v1/me/terms-of-service/decline', { version });
}
